import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCode } from '@fortawesome/free-solid-svg-icons';

export interface DownloadMenuPopoverProps {
  /** OMR-only: stream the stored MusicXML without merging fingerings. */
  onDownloadXmlPlain?: () => void;
  /** Standard MusicXML with fingerings merged in. */
  onDownloadXml?: () => void;
  /** Sibelius-compatible MusicXML with fingerings. */
  onDownloadXmlSibelius?: () => void;
  isDownloadingXmlPlain?: boolean;
  isDownloadingXml?: boolean;
  isDownloadingXmlSibelius?: boolean;
  onClose: () => void;
  /** The XML trigger. Outside-click ignores this so the same
   *  mousedown that closes does not immediately re-open. */
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  placement?: 'bottom' | 'top';
}

/**
 * MusicXML format menu opened from the XML download button.
 */
export const DownloadMenuPopover: React.FC<DownloadMenuPopoverProps> = ({
  onDownloadXmlPlain,
  onDownloadXml,
  onDownloadXmlSibelius,
  isDownloadingXmlPlain = false,
  isDownloadingXml = false,
  isDownloadingXmlSibelius = false,
  onClose,
  anchorRef,
  placement = 'bottom',
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!panelRef.current || panelRef.current.offsetParent === null) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [onClose, anchorRef]);

  const itemClass = `
    flex w-full cursor-pointer items-center gap-x-3 rounded-md px-3 py-2
    text-left text-sm font-medium text-gray-800 transition-colors
    hover:bg-gray-100
    focus:bg-gray-100 focus:outline-none
    disabled:cursor-not-allowed disabled:opacity-50
    disabled:hover:bg-transparent
  `;

  return (
    <div
      ref={panelRef}
      role="menu"
      aria-label="MusicXML download options"
      className={`
        absolute right-0 z-50 min-w-[280px] rounded-2xl border border-gray-200
        bg-white p-2 shadow-lg
        ${
          placement === 'top'
            ? 'bottom-[calc(100%+8px)]'
            : `top-[calc(100%+8px)]`
        }
      `}
    >
      {onDownloadXmlPlain && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onDownloadXmlPlain();
            onClose();
          }}
          disabled={isDownloadingXmlPlain}
          className={itemClass}
        >
          <FontAwesomeIcon
            icon={faFileCode}
            className="w-4 text-base text-gray-600"
          />
          <span>
            {isDownloadingXmlPlain ? 'Downloading MusicXML…' : 'MusicXML'}
          </span>
        </button>
      )}

      {onDownloadXml && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onDownloadXml();
            onClose();
          }}
          disabled={isDownloadingXml}
          className={itemClass}
        >
          <FontAwesomeIcon
            icon={faFileCode}
            className="w-4 text-base text-gray-600"
          />
          <span>
            {isDownloadingXml
              ? 'Downloading MusicXML…'
              : 'MusicXML with Fingerings'}
          </span>
        </button>
      )}

      {onDownloadXmlSibelius && (
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onDownloadXmlSibelius();
            onClose();
          }}
          disabled={isDownloadingXmlSibelius}
          className={itemClass}
        >
          <FontAwesomeIcon
            icon={faFileCode}
            className="w-4 text-base text-gray-600"
          />
          <span>
            {isDownloadingXmlSibelius
              ? 'Downloading MusicXML…'
              : 'MusicXML with Fingerings (Sibelius)'}
          </span>
        </button>
      )}
    </div>
  );
};
